import { Link } from "react-router-dom";
import { ArrowUpRight, Sparkles } from "lucide-react";
import { PILLARS, VALUES, PARTNERSHIP_IMAGE, TEAM_IMAGE } from "@/data/rapro";

export default function About() {
  return (
    <div data-testid="about-page">
      {/* HERO */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 pt-16 md:pt-24 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8 reveal">
            <div className="label-eyebrow text-slate-500" data-testid="about-eyebrow">
              <span className="inline-block h-[2px] w-6 bg-[#00E676] align-middle mr-3" />
              About RAPRO Solutions
            </div>
            <h1 data-testid="about-headline" className="font-display mt-6 text-4xl md:text-6xl lg:text-7xl font-extrabold tracking-tighter text-slate-900 leading-[0.95]">
              Your trusted partner in building the <span className="text-[#00E676]">future</span> of your organisation.
            </h1>
          </div>
          <div className="lg:col-span-4 lg:pl-8">
            <p className="text-slate-600 text-lg leading-relaxed">
              With years of industry experience, we connect businesses with top-tier talent across sectors — ensuring both employers and candidates thrive.
            </p>
          </div>
        </div>
      </section>

      {/* IMAGE STRIP */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid grid-cols-2 gap-6">
          <img src={PARTNERSHIP_IMAGE} alt="Business partnership handshake" data-testid="about-image-1" className="w-full h-[280px] md:h-[400px] object-cover border-2 border-slate-900" />
          <img src={TEAM_IMAGE} alt="Recruiters at work" data-testid="about-image-2" className="w-full h-[280px] md:h-[400px] object-cover border-2 border-slate-900 translate-y-6" />
        </div>
      </section>

      {/* MISSION */}
      <section data-testid="mission-section" className="mx-auto max-w-7xl px-5 sm:px-8 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="label-eyebrow text-slate-500">
              <span className="inline-block h-[2px] w-6 bg-[#00E676] align-middle mr-3" />
              Our Mission
            </div>
          </div>
          <div className="lg:col-span-8">
            <p className="font-display text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 leading-tight">
              To deliver innovative, tailored recruitment solutions that bridge the gap between exceptional talent and forward-thinking companies — helping organisations grow by hiring the right people at the right time.
            </p>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="bg-slate-50 border-y border-slate-200" data-testid="about-pillars-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-24 md:py-32">
          <div className="max-w-2xl">
            <div className="label-eyebrow text-slate-500">
              <span className="inline-block h-[2px] w-6 bg-[#00E676] align-middle mr-3" />
              What Sets Us Apart
            </div>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight mt-4 text-slate-900">
              Four standards we never compromise.
            </h2>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-px bg-slate-200">
            {PILLARS.map((p) => (
              <div key={p.num} data-testid={`about-pillar-${p.num}`} className="bg-white p-8 md:p-10 group hover:bg-[#00E676] transition-colors duration-150">
                <div className="flex items-start justify-between">
                  <span className="font-display text-4xl font-extrabold text-[#00C853] group-hover:text-slate-900">{p.num}</span>
                  <Sparkles className="h-5 w-5 text-slate-300 group-hover:text-slate-900 transition-colors" />
                </div>
                <h3 className="font-display text-2xl font-bold mt-6 text-slate-900">{p.title}</h3>
                <p className="mt-3 text-slate-600 group-hover:text-slate-900 leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-24 md:py-32" data-testid="values-section">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="label-eyebrow text-slate-500">
              <span className="inline-block h-[2px] w-6 bg-[#00E676] align-middle mr-3" />
              Our Values
            </div>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight mt-4 text-slate-900">
              The principles behind every introduction.
            </h2>
          </div>
          <div className="lg:col-span-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-0 border-t border-l border-slate-200">
              {VALUES.map((v) => (
                <div key={v.title} data-testid={`value-${v.title.toLowerCase()}`} className="border-r border-b border-slate-200 p-8">
                  <div className="flex items-center gap-3">
                    <span className="h-2 w-2 bg-[#00E676]" />
                    <h3 className="font-display text-xl font-bold text-slate-900">{v.title}</h3>
                  </div>
                  <p className="mt-3 text-slate-600 leading-relaxed">{v.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* APPROACH BAND */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20" data-testid="approach-band">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-8">
              <h2 className="font-display text-2xl md:text-3xl lg:text-4xl font-extrabold tracking-tight text-slate-900">
                Proactive · Transparent · Personalised
              </h2>
              <p className="mt-4 text-slate-600 text-lg leading-relaxed max-w-3xl">
                By understanding your unique needs and leveraging our network, tools and insights, we deliver the best talent — whether for one role or a full team.
              </p>
            </div>
            <div className="lg:col-span-4 flex lg:justify-end">
              <Link to="/contact" data-testid="about-cta" className="inline-flex items-center gap-2 bg-[#00E676] text-slate-900 px-6 py-3.5 font-semibold hover:bg-[#00C853] transition-colors">
                Partner With Us
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
